import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  Clock,
  CheckCircle2,
  XCircle,
  MapPin,
  Trash2,
  ChevronDown,
  Loader2,
  FileText,
  Pencil,
  Navigation2,
  Coins,
  TrendingUp,
} from 'lucide-react';
import {
  listContributions,
  getContribution,
  getContributionEarnings,
  deleteContribution,
  type ContributionSummary,
  type ContributionDetail,
  type UserContributionEarnings,
} from '../../services/api';
import { DynamicIcon } from '../ui/DynamicIcon';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

const STATUS_CONFIG = {
  pending: {
    label: 'Under Review',
    icon: Clock,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle2,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  },
};

const FEATURE_TYPE_ICONS: Record<string, string> = {
  official_area: 'landmark',
  place: 'map',
  building: 'building-2',
  poi: 'map-pin',
  transport_stop: 'bus',
  road: 'route',
  landuse: 'trees',
  water: 'droplets',
  waterway: 'waves',
  landmark: 'landmark',
  structure: 'construction',
  traffic_signal: 'traffic-cone',
  tree: 'tree-pine',
  landcover: 'mountain',
  aeroway: 'plane',
  motorway_junction: 'signpost',
};

const MONTH_OPTIONS = [
  { value: '', label: 'All months' },
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
] as const;

function currentYear(): number {
  return new Date().getFullYear();
}

function buildYearOptions(): number[] {
  const anchor = currentYear();
  return Array.from({ length: 6 }, (_, i) => anchor - 2 + i);
}

function formatPoints(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1).replace(/\.0$/, '');
}

function pointsLabel(c: ContributionSummary): string | null {
  if (c.status === 'approved' && c.points_awarded != null) {
    return `${formatPoints(c.points_awarded)} pt`;
  }
  if (c.status === 'pending' && c.suggested_points != null && c.suggested_points > 0) {
    return `${formatPoints(c.suggested_points)} est.`;
  }
  return null;
}

interface MyContributionsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateBack?: () => void;
  onViewOnMap?: (detail: ContributionDetail) => void;
  onEditContribution?: (detail: ContributionDetail) => void;
}

export function MyContributionsPanel({ isOpen, onClose, onNavigateBack, onViewOnMap, onEditContribution }: MyContributionsPanelProps) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selectedYear, setSelectedYear] = useState(currentYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [contributions, setContributions] = useState<ContributionSummary[]>([]);
  const [earnings, setEarnings] = useState<UserContributionEarnings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showActivityBreakdown, setShowActivityBreakdown] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ContributionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const detailCacheRef = useRef<Map<string, ContributionDetail>>(new Map());
  const detailRequestRef = useRef(0);

  const reviewedMonth = selectedMonth ? `${selectedYear}-${selectedMonth}` : undefined;
  const skipPeriodFilter = filter === 'pending';

  const periodLabel = useMemo(() => {
    if (earnings?.month_label) return earnings.month_label;
    return String(selectedYear);
  }, [earnings?.month_label, selectedYear]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listData, earningsData] = await Promise.all([
        listContributions({
          status: filter === 'all' ? undefined : filter,
          reviewedYear: skipPeriodFilter || reviewedMonth ? undefined : selectedYear,
          reviewedMonth: skipPeriodFilter ? undefined : reviewedMonth,
        }),
        getContributionEarnings(selectedYear, reviewedMonth),
      ]);
      setContributions(listData);
      setEarnings(earningsData);
    } catch {
      setError('Failed to load contributions');
      setContributions([]);
      setEarnings(null);
    } finally {
      setLoading(false);
    }
  }, [filter, reviewedMonth, selectedYear, skipPeriodFilter]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setSelectedId(null);
      setDetail(null);
      detailCacheRef.current.clear();
    }
  }, [isOpen, fetchData]);

  const handleViewDetail = async (id: string) => {
    if (selectedId === id) {
      setSelectedId(null);
      setDetail(null);
      return;
    }
    setSelectedId(id);

    const cached = detailCacheRef.current.get(id);
    if (cached) {
      setDetail(cached);
      setDetailLoading(false);
      return;
    }

    const requestId = ++detailRequestRef.current;
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await getContribution(id);
      detailCacheRef.current.set(id, d);
      if (detailRequestRef.current === requestId) {
        setDetail(d);
      }
    } catch {
      if (detailRequestRef.current === requestId) {
        setDetail(null);
      }
    } finally {
      if (detailRequestRef.current === requestId) {
        setDetailLoading(false);
      }
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteContribution(id);
      await fetchData();
      if (selectedId === id) {
        setSelectedId(null);
        setDetail(null);
      }
      setDeleteConfirm(null);
    } catch {
      // ignore
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewOnMap = (d: ContributionDetail) => {
    if (onViewOnMap) onViewOnMap(d);
  };

  const handleEditContribution = (d: ContributionDetail) => {
    if (onEditContribution) onEditContribution(d);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const counts = earnings?.status_counts ?? {
    all: contributions.length,
    pending: contributions.filter((c) => c.status === 'pending').length,
    approved: contributions.filter((c) => c.status === 'approved').length,
    rejected: contributions.filter((c) => c.status === 'rejected').length,
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[90] flex flex-col"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-neutral-700 pt-safe">
          <button
            type="button"
            onClick={onNavigateBack ?? onClose}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <ChevronLeft size={22} className="text-gray-700 dark:text-neutral-300" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">My Contributions</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <X size={22} className="text-gray-700 dark:text-neutral-300" />
          </button>
        </div>

        {/* Period + earnings summary */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-neutral-800 bg-gray-50/80 dark:bg-neutral-900/50 space-y-3">
          <div className="flex gap-2">
            <select
              value={String(selectedYear)}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="flex-1 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-900 dark:text-neutral-100"
              aria-label="Year"
            >
              {buildYearOptions().map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="flex-[1.4] rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-900 dark:text-neutral-100"
              aria-label="Month"
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value || 'all'} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {earnings && (
            <div className="rounded-xl border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-950/30 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300">
                    <Coins size={16} />
                    <span className="text-xs font-semibold uppercase tracking-wide">Approved rewards</span>
                  </div>
                  <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-neutral-50">
                    {formatPoints(earnings.approved_points)} <span className="text-base font-semibold text-emerald-600 dark:text-emerald-400">pts</span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-neutral-400 mt-0.5">
                    {earnings.approved_count} approved in {periodLabel} · 1 pt = 1 GHS
                  </p>
                </div>
                {earnings.pending_count > 0 && (
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center justify-end gap-1 text-amber-600 dark:text-amber-400">
                      <TrendingUp size={14} />
                      <span className="text-xs font-medium">In review</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mt-0.5">
                      {formatPoints(earnings.pending_suggested_points)} est.
                    </div>
                    <p className="text-[10px] text-gray-500 dark:text-neutral-500">{earnings.pending_count} pending</p>
                  </div>
                )}
              </div>

              {earnings.by_activity.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200/60 dark:border-emerald-800/40">
                  <button
                    type="button"
                    onClick={() => setShowActivityBreakdown((v) => !v)}
                    className="flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300"
                  >
                    <ChevronDown size={14} className={`transition-transform ${showActivityBreakdown ? 'rotate-180' : ''}`} />
                    Activity breakdown
                  </button>
                  <AnimatePresence>
                    {showActivityBreakdown && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-2 space-y-1.5">
                          {earnings.by_activity.map((item) => (
                            <div key={item.activity} className="flex items-center justify-between text-xs">
                              <span className="text-gray-600 dark:text-neutral-400 truncate pr-2">{item.label}</span>
                              <span className="text-gray-900 dark:text-neutral-200 font-medium whitespace-nowrap">
                                {item.count} · {formatPoints(item.points)} pt
                              </span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1.5 px-4 py-2 border-b border-gray-100 dark:border-neutral-800">
          {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map((f) => {
            const label = f === 'all' ? 'All' : f === 'pending' ? 'Review' : STATUS_CONFIG[f].label;
            const count = counts[f];
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`flex-1 py-1.5 rounded-full text-xs font-medium text-center transition-colors ${
                  filter === f
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400 hover:bg-gray-200 dark:hover:bg-neutral-700'
                }`}
              >
                {label} ({count})
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
          ) : error ? (
            <div className="text-center py-20 px-4">
              <p className="text-red-500 text-sm">{error}</p>
              <button type="button" onClick={fetchData} className="mt-2 text-primary-500 text-sm font-medium">
                Retry
              </button>
            </div>
          ) : contributions.length === 0 ? (
            <div className="text-center py-20 px-4">
              <FileText size={40} className="mx-auto text-gray-300 dark:text-neutral-600 mb-3" />
              <p className="text-gray-500 dark:text-neutral-400 text-sm">
                {filter === 'all'
                  ? `No contributions in ${periodLabel}.`
                  : `No ${STATUS_CONFIG[filter].label.toLowerCase()} contributions in ${periodLabel}.`}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-neutral-800">
              {contributions.map((c) => {
                const cfg = STATUS_CONFIG[c.status];
                const StatusIcon = cfg.icon;
                const isExpanded = selectedId === c.id;
                const icon = c.feature_type ? FEATURE_TYPE_ICONS[c.feature_type] : 'map-pin';
                const pts = pointsLabel(c);

                return (
                  <div key={c.id}>
                    <button
                      type="button"
                      onClick={() => handleViewDetail(c.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        isExpanded ? 'bg-gray-50 dark:bg-neutral-800/50' : 'hover:bg-gray-50 dark:hover:bg-neutral-800/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <DynamicIcon name={icon} size={18} className="text-gray-500 dark:text-neutral-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-neutral-100 truncate">{c.name}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${cfg.badge}`}>
                              <StatusIcon size={10} />
                              {cfg.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <span className="text-xs text-gray-500 dark:text-neutral-400 capitalize">
                              {c.feature_type?.replace(/_/g, ' ') || c.category}
                            </span>
                            {c.submission_kind_label && (
                              <>
                                <span className="text-xs text-gray-400 dark:text-neutral-500">·</span>
                                <span className="text-xs text-gray-500 dark:text-neutral-400">{c.submission_kind_label}</span>
                              </>
                            )}
                            <span className="text-xs text-gray-400 dark:text-neutral-500">·</span>
                            <span className="text-xs text-gray-400 dark:text-neutral-500">{formatDate(c.submitted_at)}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {pts && (
                            <span
                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                c.status === 'approved'
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                              }`}
                            >
                              {pts}
                            </span>
                          )}
                          <ChevronDown
                            size={16}
                            className={`text-gray-400 dark:text-neutral-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-4 border-l-2 border-primary-200 dark:border-primary-800 ml-[23px] pl-[37px] pr-4">
                            {detailLoading ? (
                              <div className="flex items-center gap-2 py-3">
                                <Loader2 size={14} className="animate-spin text-gray-400" />
                                <span className="text-xs text-gray-400">Loading details...</span>
                              </div>
                            ) : detail ? (
                              <div className="space-y-2">
                                {(detail.points_awarded != null || detail.suggested_activity_label) && (
                                  <div className="flex flex-wrap gap-2 text-xs">
                                    {detail.points_awarded != null && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-medium">
                                        <Coins size={12} />
                                        {formatPoints(detail.points_awarded)} pt awarded
                                      </span>
                                    )}
                                    {detail.suggested_activity_label && detail.status === 'pending' && (
                                      <span className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400">
                                        {detail.suggested_activity_label}
                                      </span>
                                    )}
                                    {detail.submission_kind_label && (
                                      <span className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-400">
                                        {detail.submission_kind_label}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {detail.address && (
                                  <div className="flex items-start gap-1.5 text-xs">
                                    <MapPin size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                    <span className="text-gray-600 dark:text-neutral-400">{detail.address}</span>
                                  </div>
                                )}

                                {detail.description && (
                                  <p className="text-xs text-gray-500 dark:text-neutral-400 italic">"{detail.description}"</p>
                                )}

                                {detail.photos && detail.photos.length > 0 && (
                                  <div className="flex gap-2 mt-1">
                                    {detail.photos.map((url, i) => (
                                      <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-neutral-700 flex-shrink-0"
                                      >
                                        <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                )}

                                {detail.reviewer_feedback && (
                                  <div className={`text-xs p-2 rounded-lg border ${STATUS_CONFIG[detail.status].bg} ${STATUS_CONFIG[detail.status].border}`}>
                                    <span className="font-medium">Reviewer:</span> {detail.reviewer_feedback}
                                  </div>
                                )}

                                <div className="flex items-center gap-3 text-[10px] text-gray-400 dark:text-neutral-500 pt-1">
                                  <span>Submitted {formatDate(detail.submitted_at)} at {formatTime(detail.submitted_at)}</span>
                                  {detail.reviewed_at && <span>· Reviewed {formatDate(detail.reviewed_at)}</span>}
                                </div>

                                <div className="flex items-center gap-3 pt-1 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => handleViewOnMap(detail)}
                                    className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600"
                                  >
                                    <Navigation2 size={12} />
                                    View on map
                                  </button>

                                  {(c.status === 'rejected' || c.status === 'approved') && (
                                    <button
                                      type="button"
                                      onClick={() => handleEditContribution(detail)}
                                      className={`flex items-center gap-1 text-xs ${
                                        c.status === 'approved'
                                          ? 'text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300'
                                          : 'text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300'
                                      }`}
                                    >
                                      <Pencil size={12} />
                                      {c.status === 'approved' ? 'Revise' : 'Edit & Resubmit'}
                                    </button>
                                  )}

                                  {c.status === 'pending' && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleEditContribution(detail)}
                                        className="flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400 hover:text-primary-600 dark:hover:text-primary-300"
                                      >
                                        <Pencil size={12} />
                                        Edit
                                      </button>
                                      {deleteConfirm === c.id ? (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-red-500">Withdraw?</span>
                                          <button
                                            type="button"
                                            onClick={() => handleDelete(c.id)}
                                            disabled={deletingId === c.id}
                                            className="text-xs text-red-600 font-medium hover:underline"
                                          >
                                            {deletingId === c.id ? 'Deleting...' : 'Yes'}
                                          </button>
                                          <button type="button" onClick={() => setDeleteConfirm(null)} className="text-xs text-gray-500 hover:underline">
                                            No
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => setDeleteConfirm(c.id)}
                                          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                                        >
                                          <Trash2 size={12} />
                                          Withdraw
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 py-2">Could not load details.</p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
